import type { CSSProperties } from 'react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDemo2Ctx } from '../Demo2Context';
import Demo2PhotoLightbox from '../Demo2PhotoLightbox';
import { TYPE_ICON_RI, workHistoryPhoto } from '../demo2Data';

export default function Demo2MHome() {
    const navigate = useNavigate();
    const { workOrders, alerts, workHistory } = useDemo2Ctx();
    const [lightboxIdx, setLightboxIdx] = useState<number | null>(null);
    const recentHistory = workHistory.slice(0, 6);
    const recentPhotos = recentHistory.map((_, i) => workHistoryPhoto(i));

    const myAll = workOrders.filter(w => w.assignee === '박현장');
    const mine = myAll.filter(w => w.status !== 'done');
    const doneToday = myAll.filter(w => w.status === 'done').length;
    const urgentMine = mine.filter(w => w.priority === 'urgent').length;
    const myAlerts = alerts.filter(a => !a.read && a.level === 'danger').slice(0, 1);

    const totalToday = doneToday + mine.length;
    const progressPct = totalToday === 0 ? 0 : Math.round((doneToday / totalToday) * 100);
    const r = 18, circumference = 2 * Math.PI * r;
    const offset = circumference - (progressPct / 100) * circumference;

    return (
        <div className="m-body">
            <div className="m-body-p">
                <div className="m-home-hero">
                    <div className="m-home-hero-pattern" />
                    <div className="m-home-hero-top">
                        <div>
                            <div className="m-home-hero-hi">안녕하세요</div>
                            <div className="m-home-hero-name">박현장님 👋</div>
                            <span className="m-home-hero-date"><i className="ri-calendar-line" /> 2026년 7월 20일(월)</span>
                        </div>
                        <div className="m-home-hero-avatar">박</div>
                    </div>
                    <div className="m-home-hero-stats">
                        <div className="m-home-hero-stat"><div className="m-home-hero-stat-val">{mine.length}</div><div className="m-home-hero-stat-lbl">배정 작업</div></div>
                        <div className="m-home-hero-div" />
                        <div className="m-home-hero-stat"><div className="m-home-hero-stat-val">{doneToday}</div><div className="m-home-hero-stat-lbl">완료</div></div>
                        <div className="m-home-hero-div" />
                        <div className="m-home-hero-stat"><div className="m-home-hero-stat-val" style={urgentMine > 0 ? { color: 'var(--danger)' } : {}}>{urgentMine}</div><div className="m-home-hero-stat-lbl">긴급</div></div>
                    </div>
                </div>

                <div className="m-progress-card">
                    <div className="m-progress-ring">
                        <svg viewBox="0 0 44 44">
                            <circle cx={22} cy={22} r={r} fill="none" stroke="var(--border)" strokeWidth={4.5} />
                            <circle cx={22} cy={22} r={r} fill="none" stroke="var(--primary)" strokeWidth={4.5} strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={offset} style={{ transition: 'stroke-dashoffset 0.6s ease' }} />
                        </svg>
                        <div className="m-progress-ring-val">{progressPct}%</div>
                    </div>
                    <div>
                        <div className="m-progress-txt-ttl">오늘의 작업 진행률</div>
                        <div className="m-progress-txt-sub">{totalToday === 0 ? '오늘 배정된 작업이 없습니다' : `${totalToday}건 중 ${doneToday}건 완료`}</div>
                    </div>
                </div>

                <div className="m-quick-row">
                    <div className="m-quick" onClick={() => navigate('/demo2/mobile/map')}><div className="m-quick-ico d2-ico-blue"><i className="ri-map-pin-line" /></div><div className="m-quick-lbl">지도</div></div>
                    <div className="m-quick" onClick={() => navigate('/demo2/mobile/worklist')}><div className="m-quick-ico d2-ico-purple"><i className="ri-file-list-3-line" /></div><div className="m-quick-lbl">작업 등록</div></div>
                    <div className="m-quick" onClick={() => navigate('/demo2/mobile/moisture')}><div className="m-quick-ico d2-ico-teal"><i className="ri-drop-line" /></div><div className="m-quick-lbl">수분 확인</div></div>
                    <div className="m-quick" onClick={() => navigate('/demo2/mobile/history')}><div className="m-quick-ico d2-ico-orange"><i className="ri-history-line" /></div><div className="m-quick-lbl">내 이력</div></div>
                </div>

                {myAlerts.map(a => (
                    <div key={a.id} className="m-alert-banner" onClick={() => navigate('/demo2/mobile/moisture')}><i className="ri-alarm-warning-line" /> {a.title}</div>
                ))}

                <div className="m-section-ttl">오늘의 작업 <span className="cnt">{mine.length}건</span></div>
                {mine.length === 0 && (
                    <div className="d2-empty">
                        <i className="ri-checkbox-circle-line" />
                        <div className="d2-empty-txt">오늘 배정된 작업이 없습니다</div>
                    </div>
                )}
                {mine.map(w => (
                    <div
                        key={w.id}
                        className="m-worklist-item"
                        style={{ '--wl-color': w.priority === 'urgent' ? 'var(--danger)' : w.priority === 'high' ? 'var(--warn)' : 'var(--primary)' } as CSSProperties}
                        onClick={() => navigate(`/demo2/mobile/work-process/${w.id}`)}
                    >
                        <div className="m-wl-top">
                            <span className="m-wl-title"><span className="m-wl-title-ico"><i className={TYPE_ICON_RI[w.type]} /></span>{w.title}</span>
                            <span className={`badge badge-${w.status === 'progress' ? 'caution' : 'neutral'}`}>{w.status === 'progress' ? '진행중' : '대기'}</span>
                        </div>
                        <div className="m-wl-meta"><span><i className="ri-map-pin-line" /> {w.zoneName}</span><span><i className="ri-calendar-line" /> {w.due}</span></div>
                        <i className="ri-arrow-right-s-line m-wl-chevron" />
                    </div>
                ))}

                <div className="m-section-ttl">
                    최근 작업이력
                    <span className="m-section-link" onClick={() => navigate('/demo2/mobile/history')}>전체 보기 <i className="ri-arrow-right-s-line" /></span>
                </div>
                {recentHistory.length === 0 && (
                    <div className="d2-empty">
                        <i className="ri-history-line" />
                        <div className="d2-empty-txt">작업 이력이 없습니다</div>
                    </div>
                )}
                <div className="m-history-scroll">
                    {recentHistory.map((h, i) => {
                        const photo = recentPhotos[i];
                        const delta = h.after - h.before;
                        return (
                            <div key={i} className="m-history-card" onClick={() => setLightboxIdx(i)}>
                                <div className="m-history-card-img">
                                    <img src={photo.src} alt={photo.caption} loading="lazy" />
                                    <span className="m-history-card-type"><i className={TYPE_ICON_RI[h.type] || 'ri-file-list-3-line'} /> {h.type}</span>
                                </div>
                                <div className="m-history-card-body">
                                    <div className="m-history-card-zone">{h.zoneName}</div>
                                    <div className="m-history-card-meta">
                                        <span>{h.date.slice(5, 10)}</span>
                                        <span style={{ color: delta >= 0 ? 'var(--good)' : 'var(--danger)' }}>{h.before}%→{h.after}%</span>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
            {lightboxIdx !== null && (
                <Demo2PhotoLightbox photos={recentPhotos} index={lightboxIdx} onClose={() => setLightboxIdx(null)} onIndexChange={setLightboxIdx} />
            )}
        </div>
    );
}
